import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { i18n } from '@lingui/core'
import { defineMessage, t } from '@lingui/macro'
import { addYears } from 'date-fns'
import { isPast } from 'date-fns/esm'
import { Form, Formik } from 'formik'
import { isEmpty } from 'ramda'
import { useCallback } from 'react'
import styled, { useTheme } from 'styled-components'
import { useMediaQuery } from 'usehooks-ts'

import { Checkbox } from '@/@bothrs-ui/web/components/forms/checkbox/Checkbox'
import { DateInput } from '@/@bothrs-ui/web/components/forms/date-input/DateInput'
import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Line } from '@/@bothrs-ui/web/components/primitives/line/Line'
import { SlidingSidebar } from '@/@bothrs-ui/web/components/sliding-sidebar/SlidingSidebar'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H2 } from '@/@bothrs-ui/web/components/typography/Heading'
import { FullScreenModalLayout } from '@/@bothrs-ui/web/full-screen-modal-layout/FullScreenModalLayout'
import { breakpoints } from '@/config/layout'
import { mixpanel } from '@/mixpanel'
import { formatLocale } from '@/modules/shared/helpers/locale'
import { FilterTimeInput } from './FilterTimeInput'

import type { FlowFilters, FlowState } from '../types/booking.type'
export const Weekdays = {
  '1': defineMessage({ id: 'globals.monday', message: 'Maandag' }),
  '2': defineMessage({ id: 'globals.tuesday', message: 'Dinsdag' }),
  '3': defineMessage({ id: 'globals.wednesday', message: 'Woensdag' }),
  '4': defineMessage({ id: 'globals.thursday', message: 'Donderdag' }),
  '5': defineMessage({ id: 'globals.friday', message: 'Vrijdag' }),
  '6': defineMessage({ id: 'globals.saturday', message: 'Zaterdag' }),
}

type FilterModalProps = {
  isOpen: boolean
  flowState: FlowState
  filters: FlowFilters
  onClose: () => void
  onUpdate: (action: 'update-filters', data?: { filters: FlowFilters }) => void
}

export const defaultFilter: FlowFilters = {
  locations: {},
  fromDate: formatLocale(new Date(), 'yyyy-MM-dd'),
  doctors: {},
  preferredDates: {
    1: {
      dayNumber: 1,
      startDate: '00:00',
      endDate: '23:59',
      checked: false,
    },
    2: {
      dayNumber: 2,
      startDate: '00:00',
      endDate: '23:59',
      checked: false,
    },
    3: {
      dayNumber: 3,
      startDate: '00:00',
      endDate: '23:59',
      checked: false,
    },
    4: {
      dayNumber: 4,
      startDate: '00:00',
      endDate: '23:59',
      checked: false,
    },
    5: {
      dayNumber: 5,
      startDate: '00:00',
      endDate: '23:59',
      checked: false,
    },
    6: {
      dayNumber: 6,
      startDate: '00:00',
      endDate: '23:59',
      checked: false,
    },
  },
}

const FilterModal = ({ isOpen, flowState, onClose, onUpdate }: FilterModalProps) => {
  const theme = useTheme()
  const isMinMedium = useMediaQuery(breakpoints.fromM)

  const earliestDate = isPast(new Date(flowState.filters?.fromDate || ''))
    ? formatLocale(new Date(), 'yyyy-MM-dd')
    : flowState.filters?.fromDate

  // Cap the start date one year ahead of today so the extended-search ceiling
  // (18 months from today) is always reachable from the chosen start date.
  const maxDate = formatLocale(addYears(new Date(), 1), 'yyyy-MM-dd')

  const filters = flowState.filters ?? defaultFilter

  // Interaction Methods
  // ------------------------------------------------------------------------- /
  const handleGoBack = useCallback(() => {
    onClose()
  }, [onClose])

  const handleSubmit = async (values: FlowFilters) => {
    onUpdate('update-filters', {
      filters: values,
    })

    mixpanel.track('booking_flow_filters_change', {
      step: 'filter',
      filters: values,
      withTriage: !!flowState.triagePath?.length,
    })

    onClose()
  }

  const content = (
    <Formik initialValues={filters} enableReinitialize onSubmit={handleSubmit}>
      {({ values = defaultFilter }) => {
        return (
          <StForm>
            <StContentContainer gap={theme.UI.Spacing.Space6}>
              <StHeaderContainer gap={theme.UI.Spacing.Space5}>
                <FontAwesomeIcon
                  icon={['far', 'close']}
                  fontSize={20}
                  color={theme.Colors.Neutrals.Darker}
                  onClick={handleGoBack}
                  style={{ alignSelf: 'flex-end' }}
                />
                <FlexColumn gap={theme.UI.Spacing.Space2}>
                  <H2 noMargin>
                    {t({
                      id: 'appointments.book.slots.filter.title',
                      comment: 'Title text',
                      message: 'Filter resultaten',
                    })}
                  </H2>
                  {flowState.specialisation?.title?.nl ? (
                    <SpecialisationTag type='BodyMediumRegular'>{flowState.specialisation.title.nl}</SpecialisationTag>
                  ) : null}
                </FlexColumn>
              </StHeaderContainer>
              <FlexColumn gap={theme.UI.Spacing.Space8}>
                {/* Locations */}
                {isEmpty(values?.locations || {}) ? null : (
                  <FlexColumn gap={theme.UI.Spacing.Space3}>
                    <Span type='BodyLargeBold'>
                      {t({
                        id: 'appointments.book.slots.filter.location',
                        message: 'Locatie',
                      })}
                    </Span>
                    <Line />
                    <StCheckboxGroup role='group'>
                      {Object.entries(values?.locations || {}).map(([key, location]) => {
                        return (
                          <Checkbox
                            type='checkbox'
                            name={`locations.${key}.checked`}
                            id={location.id.toString()}
                            key={`location-${key}`}
                            label={location.name}
                          />
                        )
                      })}
                    </StCheckboxGroup>
                  </FlexColumn>
                )}

                {/* FromDate */}
                <FlexColumn gap={theme.UI.Spacing.Space3}>
                  <Span type='BodyLargeBold'>
                    {t({
                      id: 'appointments.book.slots.filter.start-date',
                      message: 'Zoek een afspraak vanaf',
                    })}
                  </Span>
                  <DateInput
                    type={'date'}
                    name={'fromDate'}
                    min={earliestDate}
                    value={earliestDate || formatLocale(new Date(), 'yyyy-MM-dd')}
                    max={maxDate}
                    noMargin={true}
                  />
                </FlexColumn>

                {/* Day preference */}
                <FlexColumn gap={theme.UI.Spacing.Space3}>
                  <Span type='BodyLargeBold'>
                    {t({
                      id: 'appointments.book.step3.slots.filter.day-preference',
                      comment: 'Day preference title',
                      message: 'Voorkeursdag(en) en uur',
                    })}
                  </Span>
                  <Line />
                  {Object.entries(values?.preferredDates ?? {}).map(([key, dayObject]) => {
                    const label = i18n._(Weekdays[key as keyof typeof Weekdays])

                    return (
                      <div key={key}>
                        <FilterTimeInput
                          name={`preferredDates.${key}`}
                          label={label}
                          id={key}
                          checked={!!dayObject.checked}
                          startDate={dayObject.startDate}
                          endDate={dayObject.endDate}
                        />
                      </div>
                    )
                  })}
                </FlexColumn>
              </FlexColumn>
            </StContentContainer>
            <StButtonContainer>
              <Button type='button' variant='Secondary' noWrap onClick={handleGoBack}>
                {t({
                  id: 'globals.cancel',
                  message: 'Annuleren',
                })}
              </Button>
              <Button type='submit' variant='Primary' noWrap>
                {t({
                  id: 'appointments.book.step3.slots.filter.primary',
                  comment: 'Primary button title',
                  message: 'Filters opslaan',
                })}
              </Button>
            </StButtonContainer>
          </StForm>
        )
      }}
    </Formik>
  )

  if (!isMinMedium) {
    return (
      <FullScreenModalLayout isOpen={isOpen} backgroundColor={theme.Colors.Background.White}>
        {content}
      </FullScreenModalLayout>
    )
  }
  return (
    <SlidingSidebar
      backgroundColor={theme.Colors.Background.White}
      width={422}
      open={isOpen}
      onDismiss={handleGoBack}
      closeButtonVisible={false}
    >
      <StDesktopContainer width={422}>{content}</StDesktopContainer>
    </SlidingSidebar>
  )
}

export { FilterModal }

const StForm = styled(Form)`
  display: flex;
  flex-direction: column;
  flex: 1;

  overflow: hidden;
`

const StDesktopContainer = styled(FlexColumn)<{ width?: number }>`
  overflow: hidden;
  max-width: ${({ width = 422 }) => width}px;
`

const StContentContainer = styled(FlexColumn)`
  position: relative;
  padding: 0 ${({ theme }) => theme.UI.SpacingPx.Space6};

  overflow: auto;
`

const StHeaderContainer = styled(FlexColumn)`
  position: sticky;
  padding-top: max(var(--safe-area-inset-top), 20px);
  top: 0;
  z-index: 2;
  background: ${({ theme }) => theme.Colors.Background.White};
  padding-bottom: ${({ theme }) => theme.UI.SpacingPx.Space6};
`

const StCheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};
  & > * {
    display: flex;
    align-items: center !important;
    & > div:first-child {
      margin-top: 0 !important;
    }
  }
`

const StButtonContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};
  z-index: 2;
  background: ${({ theme }) => theme.Colors.Background.White};
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.UI.SpacingPx.Space4};
  padding-top: ${({ theme }) => theme.UI.SpacingPx.Space6};
  padding-bottom: calc(
    max(var(--safe-area-inset-bottom, 0px), 20px) +
      ${({ theme }) => theme.UI.Spacing.Space1}px
  );
  @media (${breakpoints.fromM}) {
    padding-bottom: calc(
      max(var(--safe-area-inset-bottom, 0px), 20px) +
        ${({ theme }) => theme.UI.Spacing.Space6}px
    );
  }

  box-shadow:
    0px 0px 70px rgba(205, 208, 213, 0.15),
    0px 0px 10px rgba(170, 179, 193, 0.12);

  @media ${breakpoints.fromS} {
    position: relative;
    padding: ${({ theme }) => theme.UI.SpacingPx.Space6};
    padding-top: 0;
    box-shadow: none;
  }
`

const SpecialisationTag = styled(Span)`
  width: fit-content;
  padding: 2px ${({ theme }) => theme.UI.SpacingPx.Space2};
  border: 1px solid ${({ theme }) => theme.Colors.Neutrals.Darker};
  border-radius: 20px;
  color: ${({ theme }) => theme.Colors.Neutrals.Darker};
  text-align: center;
`
